import { useMemo } from 'react';
import styled from 'styled-components';
import { 
  ArrowDownRight, 
  ArrowUpRight,
  Euro, 
  ShieldCheck, 
  Zap, 
  Users 
} from 'lucide-react';
import { LucideIcon } from 'lucide-react';
import * as TypographyStyles from '../TypographyStyles';
import { purpleColors } from '../ContentPages/AppLandingPage/styles';
import { slateColors } from '../ContentPages/AppLandingPage/styles';
import { TaggedJourney } from '../../utils/mapJourneysData';
import { Tag } from './Tags';
import { useTranslation } from 'react-i18next';

export type SortOption = 'arrives-first' | 'most-reliable' | 'cheapest' | 'least-busy' | 'fastest' | 'departs-first';

type SortConfig = {
  displayName: string;
  icon: LucideIcon;
  color: string;
}

const getSortConfigs = (t: any): Record<SortOption, SortConfig> => ({
  'arrives-first': {
    displayName: t('journeyCard.sortOptions.arrivesFirst'),
    icon: ArrowDownRight,
    color: '#06b6d4' // cyan
  },
  'most-reliable': {
    displayName: t('journeyCard.sortOptions.mostReliable'),
    icon: ShieldCheck,
    color: purpleColors[600] // purple
  },
  'cheapest': {
    displayName: t('journeyCard.sortOptions.cheapest'),
    icon: Euro,
    color: '#3b82f6' // blue
  },
  'least-busy': {
    displayName: t('journeyCard.sortOptions.leastBusy'),
    icon: Users,
    color: '#22c55e' // green
  },
  'fastest': {
    displayName: t('journeyCard.sortOptions.fastest'),
    icon: Zap,
    color: '#f97316' // orange
  },
  'departs-first': {
    displayName: t('journeyCard.sortOptions.departsFirst'),
    icon: ArrowUpRight,
    color: '#14b8a6' // teal
  }
})

const SortOptionsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  padding: 0 1rem;
  margin-top: 1.5rem;
  
  @media (min-width: 768px) {
    padding: 0 1.5rem;
    margin-top: 1.5rem;
  }
`

// Extend Tag styling for button with selected/unselected states
const SortButton = styled(Tag).attrs({ as: 'button' })<{ $isSelected: boolean; color: string }>`
  margin: 0;
  border: ${props => props.$isSelected ? 'none' : `1px solid ${slateColors[300]}`};
  background-color: ${props => props.$isSelected ? props.color : 'white'};
  color: ${props => props.$isSelected ? 'white' : slateColors[600]};
  cursor: pointer;
  box-sizing: border-box;
  font-family: inherit;
  font-size: inherit;
  line-height: 1;
  transition: all 0.2s;
  
  &:hover {
    ${props => !props.$isSelected && `
      border-color: ${props.color};
    `}
  }
`

type SortOptionsProps = {
  journeys: TaggedJourney[];
  selectedSort: SortOption;
  onSortChange: (sort: SortOption) => void;
  loading?: boolean;
}

const SortOptions: React.FC<SortOptionsProps> = ({ journeys, selectedSort, onSortChange, loading = false }) => {
  const { t } = useTranslation();
  
  // Filter available sort options based on journey data
  const availableSortOptions = useMemo(() => {
    const sortConfigs = getSortConfigs(t);
    
    // Show all options while loading
    if (loading) {
      return Object.keys(sortConfigs) as SortOption[];
    }
    
    // Filter based on actual journey data once loaded
    return (Object.keys(sortConfigs) as SortOption[]).filter(option => {
      switch (option) {
        case 'cheapest':
          return journeys.some(j => j.price?.amount != null);
        case 'most-reliable':
          return journeys.some(j => j.reliability?.reliabilityScore != null);
        case 'least-busy':
          return journeys.some(j => {
            const tagsData = j.tagsData;
            return tagsData?.loadFactorScore != null;
          });
        default:
          return true; // Always show time-based sort options
      }
    });
  }, [journeys, loading, t]);

  const sortConfigs = getSortConfigs(t);

  if (availableSortOptions.length === 0) return null;

  return (
    <SortOptionsContainer>
      {availableSortOptions.map((option) => {
        const config = sortConfigs[option];
        const isSelected = selectedSort === option;
        const IconComponent = config.icon;
        
        return (
          <SortButton
            key={option}
            $isSelected={isSelected}
            color={config.color}
            onClick={() => onSortChange(option)}
          >
            <IconComponent size={12} />
            <TypographyStyles.BodySRegular style={{ 
              fontSize: '12px', 
              fontWeight: 600, 
              color: isSelected ? 'white' : slateColors[600] 
            }}>
              {config.displayName}
            </TypographyStyles.BodySRegular>
          </SortButton>
        );
      })}
    </SortOptionsContainer>
  );
};

export default SortOptions;

